import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contactApi } from '../services/contactsApi';
import type { ContactCreate, ContactUpdate, ListContactsParams } from '../types';

export const contactKeys = {
  all: ['contacts'] as const,
  list: (params?: ListContactsParams) => [...contactKeys.all, 'list', params] as const,
  detail: (id: string) => [...contactKeys.all, 'detail', id] as const,
};

/**
 * Clean undefined values from params for consistent query key comparison.
 */
function cleanParams(params?: ListContactsParams): ListContactsParams | undefined {
  if (!params) return undefined;
  const cleaned = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined)
  ) as ListContactsParams;
  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
}

export function useContacts(params?: ListContactsParams, options?: { enabled?: boolean }) {
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only recompute when specific fields change
  const stableParams = useMemo(() => cleanParams(params), [
    params?.search,
    params?.organization_id,
    params?.contact_relationship,
    params?.owner_id,
    params?.country_code,
    params?.source,
    params?.tags,
    params?.include_archived,
    params?.custom_fields,
    params?.limit,
    params?.offset,
    params?.admin_scope,
  ]);

  return useQuery({
    queryKey: contactKeys.list(stableParams),
    queryFn: () => contactApi.list(stableParams),
    enabled: options?.enabled ?? true,
  });
}

export function useContact(id: string) {
  return useQuery({
    queryKey: contactKeys.detail(id),
    queryFn: () => contactApi.get(id),
    enabled: !!id,
  });
}

export function useCreateContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ContactCreate) => contactApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactKeys.all });
    },
  });
}

export function useUpdateContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ContactUpdate }) => contactApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: contactKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: contactKeys.all });
    },
  });
}

export function useDeleteContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => contactApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactKeys.all });
    },
  });
}

export function useArchiveContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => contactApi.archive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactKeys.all });
    },
  });
}

export function useUnarchiveContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => contactApi.unarchive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactKeys.all });
    },
  });
}

export function useBulkDeleteContacts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => contactApi.bulkDelete(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactKeys.all });
    },
  });
}

export function useBulkArchiveContacts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => contactApi.bulkArchive(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactKeys.all });
    },
  });
}
